// Pose engine — swappable abstraction over the landmark detector.
//
// Phase 1 ships the MediaPipe (BlazePose) implementation. The `PoseEngine`
// interface is the seam at which a native Apple Vision Capacitor plugin can be
// dropped in for Phase 3 without touching the analytical core or UI.

import {
  FilesetResolver,
  PoseLandmarker,
  type PoseLandmarkerResult,
} from "@mediapipe/tasks-vision";
import type { Landmark, PoseFrame } from "./landmarks";

/** Stamped on every result for longitudinal-comparability guarantees. */
export const POSE_MODEL_VERSION = "blazepose-full-0.10.18";

export type ModelVariant = "full" | "lite";

export interface PoseEngineInit {
  variant?: ModelVariant;
  /** Path to bundled wasm fileset (offline). Defaults to local /models/wasm. */
  wasmPath?: string;
}

export interface ProcessOptions {
  /** Cap analysis FPS for cross-session consistency (default 60). */
  maxFps?: number;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

export interface PoseEngine {
  readonly modelVersion: string;
  init(opts?: PoseEngineInit): Promise<void>;
  /** Decode a recorded clip and return one PoseFrame per analyzed frame. */
  processVideo(video: HTMLVideoElement, opts?: ProcessOptions): Promise<PoseFrame[]>;
  close(): void;
}

const MODEL_PATHS: Record<ModelVariant, string> = {
  full: "/models/pose_landmarker_full.task",
  lite: "/models/pose_landmarker_lite.task",
};

// For strict-offline iOS, set this to a bundled local path (see public/models/README.md).
const DEFAULT_WASM_PATH =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm";

export class MediaPipePoseEngine implements PoseEngine {
  readonly modelVersion = POSE_MODEL_VERSION;
  private landmarker: PoseLandmarker | null = null;

  async init(opts: PoseEngineInit = {}): Promise<void> {
    const variant = opts.variant ?? "full";
    const fileset = await FilesetResolver.forVisionTasks(
      opts.wasmPath ?? DEFAULT_WASM_PATH,
    );
    this.landmarker = await PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: MODEL_PATHS[variant],
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
      outputSegmentationMasks: false,
    });
  }

  async processVideo(
    video: HTMLVideoElement,
    opts: ProcessOptions = {},
  ): Promise<PoseFrame[]> {
    if (!this.landmarker) throw new Error("PoseEngine not initialized");
    const maxFps = opts.maxFps ?? 60;
    const step = 1 / maxFps;
    const duration = video.duration;
    if (!isFinite(duration) || duration <= 0) {
      throw new Error("Video has no readable duration");
    }

    const frames: PoseFrame[] = [];
    for (let t = 0; t < duration; t += step) {
      if (opts.signal?.aborted) throw new DOMException("Aborted", "AbortError");
      await seekTo(video, t);
      const tMs = t * 1000;
      const result = this.landmarker.detectForVideo(video, tMs);
      const frame = toPoseFrame(result, tMs);
      if (frame) frames.push(frame);
      opts.onProgress?.(Math.min(t / duration, 1));
    }
    opts.onProgress?.(1);
    return frames;
  }

  close(): void {
    this.landmarker?.close();
    this.landmarker = null;
  }
}

function toPoseFrame(result: PoseLandmarkerResult, tMs: number): PoseFrame | null {
  const lms = result.landmarks?.[0];
  if (!lms || lms.length < 33) return null;
  const landmarks: Landmark[] = lms.map((p) => ({
    x: p.x,
    y: p.y,
    z: p.z ?? 0,
    visibility: p.visibility ?? 1,
  }));
  const w = result.worldLandmarks?.[0];
  const world: Landmark[] | undefined = w?.map((p) => ({
    x: p.x,
    y: p.y,
    z: p.z ?? 0,
    visibility: p.visibility ?? 1,
  }));
  return { tMs, landmarks, world };
}

/** Seek a video element and resolve once the frame is ready. */
function seekTo(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked);
      resolve();
    };
    video.addEventListener("seeked", onSeeked);
    video.currentTime = Math.min(t, Math.max(0, video.duration - 1e-3));
  });
}
